// The chat inside one sourcing event (Lane 1322, slice D). The state and
// transport are the shared workspace chat (Lane 1355); this file says only what
// is particular to an event: its routes, which cards reload the workspace, and
// what the workspace shows after a turn.
import { useMemo, useRef, useState } from 'react';
import useWorkspaceChat from '../../components/workspace-chat/useWorkspaceChat';

// A change, a saved comparison or a feedback draft: reload the workspace
// figures (and the Offers tab) from the server.
const RELOAD_CARD_TYPES = ['change', 'comparison', 'feedback_draft'];
const reloadForCard = (card) => RELOAD_CARD_TYPES.includes(card.type);

// The newest card of a turn that names a tab and an item to show (UI redesign 4.9).
function lastFocus(cards) {
  const withFocus = (cards || []).filter((c) => c && c.focus);
  return withFocus.length ? withFocus[withFocus.length - 1].focus : null;
}

// The browser's IANA time zone (as the Schedule settings screen reads it).
export function viewerZone() {
  try { return Intl.DateTimeFormat().resolvedOptions().timeZone || null; } catch (e) { return null; }
}

export default function useSourcingChat({ api, eventId, onChanged, onFocus }) {
  // Lane 1368 (S7): while the chat can change everything, the server says so on load
  // (chat_control) and each message carries the buyer's time zone, so a schedule card
  // is checked and applied on the buyer's calendar days. Otherwise the body is as before.
  const control = useRef(false);
  // Lane 1368 (S4): an event built from the Home chat opens with a server-written note.
  const [intakeNote, setIntakeNote] = useState(null);
  // Lane 1368: each prepared card's state from the server (applied, dismissed or
  // replaced), so a reload never offers Apply on a card already settled.
  const [cardStates, setCardStates] = useState({});
  // Each message's verified revision steps (from -> to), so after a reload a card is
  // applied against the revision its own message moved to.
  const [cardSteps, setCardSteps] = useState({});
  // Lane 1388: the choice each applied card applied ("Add only A"), from its saved
  // receipt, and the example requests built from this event (absent: generic ones).
  const [cardChoices, setCardChoices] = useState({});
  const [examples, setExamples] = useState(null);
  const [controlOn, setControlOn] = useState(false);
  const transport = useMemo(() => ({
    load: () => api.chat(eventId).then((r) => {
      control.current = Boolean(r && r.chat_control === true);
      setIntakeNote((r && r.intake_note) || null);
      setCardStates((r && r.card_states) || {});
      setCardSteps((r && r.card_steps) || {});
      setCardChoices((r && r.card_choices) || {});
      setExamples(r && Array.isArray(r.examples) && r.examples.length ? r.examples : null);
      setControlOn(control.current);
      return r;
    }),
    create: () => api.newConversation(eventId),
    send: (body, onEvent) => {
      const zone = control.current ? viewerZone() : null;
      return api.chatTurn(eventId, zone ? { ...body, time_zone: zone } : body, onEvent);
    },
  }), [api, eventId]);
  const chat = useWorkspaceChat({ transport, onChanged, onFocus, reloadForCard, focusFromCards: lastFocus,
    chatSurface: 'sourcing' });
  // Rejects when the server could not save it, so the panel never shows a dismissal
  // a reload would undo (Codex card states r1 #3).
  const dismissCard = (proposalId) => (control.current && api.dismissCard
    ? api.dismissCard(eventId, proposalId)
    : Promise.resolve());
  return { ...chat, intakeNote, cardStates, cardSteps, cardChoices, examples, controlOn, dismissCard };
}
